import React, { useState, useEffect } from "react";
import { getAssignments, createAssignment, getSubmissions, gradeSubmission } from "../services/assignments";
import api from "../services/api";
import toast, { Toaster } from "react-hot-toast";
import Sidebar from "../components/Sidebar";

import { Calendar, Plus, User, FileText, Code, CheckCircle, Clock, AlertCircle, Star, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function TeacherAssignments({ defaultCourseId, isEmbedded }) {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [assignments, setAssignments] = useState([]);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "", type: "file", deadline: "", max_points: 100, coding_problem_id: "" });
  const [problems, setProblems] = useState([]); // for coding dropdown
  
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [activeSub, setActiveSub] = useState(null); // To show grading modal
  
  const [gradeForm, setGradeForm] = useState({ score: "", feedback: "" });

  useEffect(() => {
    api.get("/courses").then(res => {
      // Filter by teacher ID via backend in actual implementation, but local placeholder here
      setCourses(res.data);
      if(res.data.length > 0) {
        const firstCourseId = defaultCourseId ? Number(defaultCourseId) : res.data[0].id;
        setSelectedCourse(res.data.find(c => c.id === firstCourseId) || res.data[0]);
      }
    });
    
    // Preload coding problems just in case
    api.get("/coding/problems").then(res => setProblems(res.data)).catch(()=>{});
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      getAssignments(selectedCourse.id).then(res => setAssignments(res.data));
    }
  }, [selectedCourse]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createAssignment({
        ...formData,
        course_id: selectedCourse.id,
        coding_problem_id: formData.type === "coding" ? parseInt(formData.coding_problem_id) : null
      });
      toast.success("Assignment published!");
      setShowCreateModal(false);
      // reload
      const res = await getAssignments(selectedCourse.id);
      setAssignments(res.data);
    } catch (err) {
      toast.error("Publish failed");
    }
  };

  const viewSubmissions = async (assign) => {
    setSelectedAssignment(assign);
    try {
      const res = await getSubmissions(assign.id);
      setSubmissions(res.data);
    } catch (err) {
      toast.error("Could not load submissions");
    }
  };

  const handleGrade = async (e) => {
    e.preventDefault();
    try {
      await gradeSubmission(activeSub.id, {
        score: parseFloat(gradeForm.score),
        feedback: gradeForm.feedback
      });
      toast.success("Graded successfully!");
      setActiveSub(null);
      // refresh list
      viewSubmissions(selectedAssignment);
    } catch (err) {
      toast.error("Failed to grade");
    }
  };

  return (
    <div className={isEmbedded ? "w-full" : "flex min-h-screen bg-slate-50 dark:bg-slate-950"}>
      {!isEmbedded && <Sidebar role="teacher" />}
      <main className={`flex-1 p-6 dark:text-white ${isEmbedded ? "" : "lg:ml-64"}`}>
        {!isEmbedded && <Toaster />}
      
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Assignments Manager</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mt-1">Manage tasks, homework and grading workloads</p>
        </div>
        
        {!isEmbedded && (
        <div className="flex gap-3">
          <select 
            className="bg-white dark:bg-slate-900 border dark:border-slate-800 px-4 py-2 rounded-xl font-bold shadow-sm text-sm"
            onChange={(e) => setSelectedCourse(courses.find(c => c.id === parseInt(e.target.value)))}
            value={selectedCourse?.id || ""}
          >
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-5 py-2 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
          >
            <Plus size={18} /> Create Task
          </button>
        </div>
        )}
        {isEmbedded && (
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-5 py-2 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
          >
            <Plus size={18} /> Create Task
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Col: Assignments List */}
        <div className="lg:col-span-1 space-y-4">
          <h3 className="font-black text-lg uppercase tracking-wider text-slate-400 text-xs mb-2">Course Tasks</h3>
          
          {assignments.length === 0 && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-10 border border-slate-100 dark:border-slate-800 text-center text-slate-400">
              <Calendar size={40} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm font-bold">No assignments posted yet.</p>
            </div>
          )}
          
          {assignments.map(item => (
            <motion.div
              key={item.id}
              whileHover={{ y: -2 }}
              onClick={() => viewSubmissions(item)}
              className={`cursor-pointer p-5 rounded-2xl border transition-all ${selectedAssignment?.id === item.id ? 'bg-blue-600 border-blue-600 text-white shadow-xl shadow-blue-500/20' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 dark:hover:border-blue-500 hover:shadow-md'}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className={`p-2 rounded-xl ${selectedAssignment?.id === item.id ? 'bg-white/20' : 'bg-blue-50 text-blue-600 dark:bg-slate-800'}`}>
                  {item.type === "coding" ? <Code size={20} /> : <FileText size={20} />}
                </div>
                <div className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${selectedAssignment?.id === item.id ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                  Max: {item.max_points} pts
                </div>
              </div>
              <h4 className="font-black text-lg leading-tight">{item.title}</h4>
              <div className={`mt-3 flex items-center gap-2 text-xs font-medium ${selectedAssignment?.id === item.id ? 'text-white/70' : 'text-slate-500'}`}>
                <Clock size={14} />
                Deadline: {new Date(item.deadline).toLocaleDateString()}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Right Col: Submissions Context */}
        <div className="lg:col-span-2">
          {!selectedAssignment ? (
            <div className="h-full flex flex-col items-center justify-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-10 text-slate-400 bg-slate-50/50 dark:bg-slate-900/30">
              <div className="p-4 bg-white dark:bg-slate-800 rounded-full shadow-sm mb-4">
                <ChevronRight size={32} className="opacity-30" />
              </div>
              <p className="font-bold text-slate-500">Select an assignment on the left to view student work</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-8 shadow-sm h-full flex flex-col">
               <div className="mb-6 pb-6 border-b dark:border-slate-800 flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-black">{selectedAssignment.title}</h2>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">{selectedAssignment.description}</p>
                  </div>
                  <span className="px-4 py-1.5 bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 font-black text-xs rounded-full">
                    {submissions.length} Submissions
                  </span>
               </div>
               
               <div className="flex-1 overflow-y-auto space-y-4">
                 {submissions.length === 0 && (
                    <div className="py-20 text-center text-slate-400">
                       <AlertCircle size={48} className="mx-auto mb-3 opacity-20" />
                       <p className="font-bold">No student has submitted yet.</p>
                    </div>
                 )}
                 
                 {submissions.map(sub => (
                   <div key={sub.id} className="group border dark:border-slate-800 rounded-2xl p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 bg-indigo-100 dark:bg-slate-700 text-indigo-600 dark:text-white rounded-full flex items-center justify-center font-black text-lg shadow-inner">
                          {sub.student_name[0].toUpperCase()}
                        </div>
                        <div>
                           <p className="font-black text-slate-900 dark:text-white">{sub.student_name}</p>
                           <p className="text-xs text-slate-500">{sub.student_email} • {new Date(sub.submitted_at).toLocaleString()}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                         {sub.status === "graded" ? (
                           <div className="text-right">
                              <div className="flex items-center gap-1 text-emerald-600 font-black text-sm">
                                <Star fill="currentColor" size={14} /> {sub.score} / {selectedAssignment.max_points}
                              </div>
                              <span className="text-[10px] uppercase font-bold text-slate-400">Graded</span>
                           </div>
                         ) : (
                           <span className="px-3 py-1 bg-amber-50 text-amber-600 text-[10px] font-black uppercase rounded-full border border-amber-200">Pending</span>
                         )}
                         
                         <button 
                           onClick={() => {
                             setActiveSub(sub);
                             setGradeForm({ score: sub.score || "", feedback: sub.feedback || "" });
                           }}
                           className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:shadow-md transition-all"
                         >
                           Review
                         </button>
                      </div>
                   </div>
                 ))}
               </div>
            </div>
          )}
        </div>
      </div>

      {/* MODAL: Create Assignment */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.form 
              initial={{ opacity:0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              onSubmit={handleCreate}
              className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-3xl p-8 shadow-2xl dark:border dark:border-slate-800 space-y-5"
            >
               <h2 className="text-2xl font-black">Publish New Task</h2>
               
               <div>
                 <label className="block text-xs font-black uppercase text-slate-400 mb-2">Task Title</label>
                 <input required type="text" className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 focus:ring-2 ring-blue-500 font-medium" placeholder="Intro to Algorithms HW" value={formData.title} onChange={e=>setFormData({...formData, title: e.target.value})}/>
               </div>
               
               <div>
                 <label className="block text-xs font-black uppercase text-slate-400 mb-2">Instructions</label>
                 <textarea required rows={3} className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 focus:ring-2 ring-blue-500 font-medium" placeholder="Write 500 words or solve the coding lab..." value={formData.description} onChange={e=>setFormData({...formData, description: e.target.value})}/>
               </div>

               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="block text-xs font-black uppercase text-slate-400 mb-2">Format Type</label>
                   <select className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 font-bold" value={formData.type} onChange={e=>setFormData({...formData, type: e.target.value})}>
                     <option value="file">File Upload (PDF/IMG)</option>
                     <option value="coding">Interactive Coding</option>
                   </select>
                 </div>
                 <div>
                   <label className="block text-xs font-black uppercase text-slate-400 mb-2">Max Points</label>
                   <input required type="number" className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 font-bold" value={formData.max_points} onChange={e=>setFormData({...formData, max_points: e.target.value})}/>
                 </div>
               </div>

               {formData.type === "coding" && (
                 <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <label className="block text-xs font-black uppercase text-slate-400 mb-2">Link Coding Sandbox Problem</label>
                    <select required className="w-full bg-amber-50 text-amber-800 border border-amber-200 dark:bg-slate-800 dark:text-white dark:border-slate-700 rounded-xl px-4 py-3 font-bold" value={formData.coding_problem_id} onChange={e=>setFormData({...formData, coding_problem_id: e.target.value})}>
                      <option value="">-- Select Challenge --</option>
                      {problems.map(p => <option key={p.id} value={p.id}>{p.title} ({p.difficulty})</option>)}
                    </select>
                 </motion.div>
               )}

               <div>
                 <label className="block text-xs font-black uppercase text-slate-400 mb-2">Deadline Date</label>
                 <input required type="datetime-local" className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 font-bold" value={formData.deadline} onChange={e=>setFormData({...formData, deadline: e.target.value})}/>
               </div>

               <div className="flex gap-3 pt-4">
                 <button type="button" onClick={()=>setShowCreateModal(false)} className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 rounded-xl font-bold">Cancel</button>
                 <button type="submit" className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-500/20 hover:bg-blue-700">Publish</button>
               </div>
            </motion.form>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: Grade Submission / Review */}
      <AnimatePresence>
        {activeSub && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
            <motion.div 
              initial={{ opacity:0, y: 50 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-3xl overflow-hidden flex flex-col lg:flex-row max-h-[90vh] shadow-2xl"
            >
              {/* Review Panel */}
              <div className="flex-1 p-8 bg-slate-50 dark:bg-slate-950 overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-black">Submission Detail</h3>
                  <span className="text-sm font-medium text-slate-500">{activeSub.student_name}</span>
                </div>

                {selectedAssignment.type === "coding" ? (
                   <div className="space-y-4">
                      <div className="flex items-center justify-between bg-slate-900 text-emerald-400 px-4 py-2 rounded-t-xl text-xs font-mono">
                        <span>Language: {activeSub.submitted_language || "Generic"}</span>
                      </div>
                      <pre className="p-6 bg-slate-900 text-slate-200 rounded-b-xl overflow-x-auto font-mono text-sm leading-relaxed shadow-inner border border-slate-800">
                        <code>{activeSub.submitted_code || "// No code uploaded"}</code>
                      </pre>
                   </div>
                ) : (
                   <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-2xl p-8 text-center">
                      <FileText size={64} className="mx-auto text-blue-500 opacity-50 mb-4" />
                      <h4 className="font-bold mb-1">Attached Solution</h4>
                      <p className="text-sm text-slate-500 mb-6">{activeSub.file_url?.split('/').pop() || "submission.pdf"}</p>
                      {activeSub.file_url ? (
                        <a href={activeSub.file_url} target="_blank" rel="noreferrer" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-blue-500/20 hover:bg-blue-700">View Full File</a>
                      ) : (
                        <div className="text-red-500 font-bold text-sm">No file attached.</div>
                      )}
                   </div>
                )}
              </div>

              {/* Grading Form Side */}
              <form onSubmit={handleGrade} className="w-full lg:w-[350px] p-8 border-l dark:border-slate-800 space-y-6 bg-white dark:bg-slate-900">
                 <h3 className="text-xl font-black flex items-center gap-2"><CheckCircle className="text-emerald-500" /> Grade It</h3>
                 
                 <div>
                   <label className="block text-xs font-black uppercase text-slate-400 mb-2">Score ({selectedAssignment.max_points} max)</label>
                   <input 
                     required type="number" step="0.1" 
                     className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-2xl px-4 py-4 font-black text-2xl text-indigo-600 focus:ring-2 ring-indigo-500" 
                     placeholder="85.5"
                     value={gradeForm.score} onChange={e=>setGradeForm({...gradeForm, score: e.target.value})}
                   />
                 </div>
                 
                 <div>
                   <label className="block text-xs font-black uppercase text-slate-400 mb-2">Add Feedback Notes</label>
                   <textarea 
                     rows={5} 
                     className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 text-sm font-medium" 
                     placeholder="Good effort! Refactor the loop structure for O(n) efficiency next time."
                     value={gradeForm.feedback} onChange={e=>setGradeForm({...gradeForm, feedback: e.target.value})}
                   />
                 </div>

                 <div className="pt-4 space-y-3">
                   <button type="submit" className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black shadow-xl shadow-indigo-500/20 hover:bg-indigo-700 transition-all">Submit Grade</button>
                   <button type="button" onClick={()=>setActiveSub(null)} className="w-full py-3 text-slate-400 font-bold text-sm hover:text-slate-800 dark:hover:text-white">Close Review</button>
                 </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      </main>
    </div>
  );
}
